import type {
  ParticipantRequirement,
  Deliverable,
  FinalizationPreview,
} from '@/types/productionRequirements'
import { getFunctions, httpsCallable, type Functions } from 'firebase/functions'
import { app } from './app'
import { toUserFacingError } from '@/lib/errors'
import type {
  ParticipantOfferStatus,
  ParticipantPackageConfig,
  ParticipantRosterState,
} from '@/types/content.types'

const functions: Functions = getFunctions(app)

/**
 * Keep callable failures safe for UI code. Firebase's callable client exposes
 * provider messages verbatim, so normalize them once at the shared boundary
 * while retaining the Firebase code for support and conditional flows.
 */
function createCallable<RequestData, ResponseData>(
  functionsInstance: Functions,
  name: string
): ReturnType<typeof httpsCallable<RequestData, ResponseData>> {
  const callable = httpsCallable<RequestData, ResponseData>(functionsInstance, name)
  return ((...args: Parameters<typeof callable>) =>
    callable(...args).catch((error: unknown) => {
      throw toUserFacingError(
        error,
        'We could not complete that request. Please try again.',
        `functions/${name}/failed`
      )
    })) as ReturnType<typeof httpsCallable<RequestData, ResponseData>>
}

export interface ShopOrderReceiptResponse {
  kind: 'shop'
  status: string
  lineItems: Array<{ productId: string; variantId: number; quantity: number }>
  totalCents: number
  deliveryState: 'pending' | 'failed' | 'submitted' | 'in_production' | 'shipped' | 'cancelled'
}

export interface TicketOrderReceiptResponse {
  kind: 'ticket'
  status: string
  eventTitle: string
  lineItems: Array<{ ticketTypeName: string; quantity: number; unitPriceCents: number }>
  totalCents: number
  deliveryState: 'pending' | 'sent' | 'failed'
}

export type OrderReceiptResponse = ShopOrderReceiptResponse | TicketOrderReceiptResponse

// Uncomment to connect to the Firebase Emulator Suite locally:
// import { connectFunctionsEmulator } from 'firebase/functions'
// if (import.meta.env.DEV) {
//   connectFunctionsEmulator(functions, '127.0.0.1', 5001)
// }

/** Sets or clears the deadline for an application. Schedules a Cloud Task to auto-close it. */
const setApplicationDeadline = createCallable<
  { applicationId: string; deadline: string | null },
  { taskName: string | null }
>(functions, 'setApplicationDeadline')

/** Syncs all visible Printify products into Firestore. Admin-only. */
const syncPrintifyProductsFn = createCallable<Record<string, never>, { productCount: number }>(
  functions,
  'syncPrintifyProducts'
)

/** Creates a Stripe PaymentIntent and a pending order doc in Firestore. */
const createPaymentIntentFn = createCallable<
  {
    items: Array<{ productId: string; variantId: number; quantity: number }>
    shipping: {
      firstName: string
      lastName: string
      email: string
      phone: string
      country: string
      region: string
      address1: string
      address2?: string
      city: string
      zip: string
    }
    termsAccepted: boolean
  },
  { clientSecret: string; orderId: string; receiptToken: string }
>(functions, 'createPaymentIntent')

/** Creates a Stripe PaymentIntent for a ticket order. Supports guest and authenticated checkout. */
const createTicketPaymentIntentFn = createCallable<
  {
    checkoutId: string
    eventId: string
    items: Array<{ ticketTypeId: string; quantity: number }>
    purchaserEmail: string
    purchaserFirstName: string
    purchaserLastName: string
    termsAccepted: boolean
  },
  { clientSecret: string; orderId: string; receiptToken: string }
>(functions, 'createTicketPaymentIntent')

/** Verifies a short-lived checkout capability and returns a non-sensitive receipt projection. */
const getOrderReceiptFn = createCallable<
  { kind: 'shop' | 'ticket'; orderId: string; receiptToken: string },
  OrderReceiptResponse
>(functions, 'getOrderReceipt')

/** Returns the server-derived shared seat count for the public ticket selector. */
const getPublicTicketAvailabilityFn = createCallable<
  { eventId: string },
  { remainingSharedSeats: number | null }
>(functions, 'getPublicTicketAvailability')

/** Emails paid guest ticket links without disclosing whether an address has orders. */
const requestTicketOrdersByEmailFn = createCallable<{ email: string }, { accepted: true }>(
  functions,
  'requestTicketOrdersByEmail'
)

/** Creates a Stripe PaymentIntent for a paid application submission. */
const createApplicationPaymentIntentFn = createCallable<
  {
    applicationId: string
    applicantInfo: { firstName: string; lastName: string; email: string }
    answers: Record<string, { label: string; fieldLibraryId?: string; value: unknown }>
    fileUploads?: Record<string, ApplicationUploadReceipt[]>
    tierId?: string
    termsAccepted: boolean
  },
  { clientSecret: string; submissionId: string; amountCents: number; currency: string }
>(functions, 'createApplicationPaymentIntent')

/** Validates a QR token and marks the ticket as checked in. Staff/admin only. */
const checkInTicketFn = createCallable<
  { qrToken: string },
  { purchaserName: string; ticketTypeName: string }
>(functions, 'checkInTicket')

/** Issues a Stripe refund for an order. Admin only. */
const issueRefundFn = createCallable<
  { orderId: string; ticketIds: string[] },
  { refundId: string }
>(functions, 'issueRefund')

/** Sets a user's role to staff/user and updates their assigned events. Admin only. */
const assignStaffToEventFn = createCallable<
  { userId: string; role: 'staff' | 'user'; assignedEvents: string[] },
  { success: boolean }
>(functions, 'assignStaffToEvent')

/** Prepares the itemized estimate for client acceptance. Admin only. */
const prepareEstimateFn = createCallable<{ engagementId: string }, { success: boolean }>(
  functions,
  'prepareEstimate'
)

/** Sends the itemized estimate to the client via Resend with a 7-day Accept link. Admin only. */
const sendEstimateToClientFn = createCallable<
  { engagementId: string },
  {
    success: boolean
    expiresAt: number
    recipient?: string
    messageId?: string
    remindersScheduled?: boolean
  }
>(functions, 'sendEstimateToClient')

/** Cancels an engagement, voids unpaid Stripe invoices, and clears scheduled tasks. Admin only. */
const cancelEngagementFn = createCallable<
  { engagementId: string },
  { stripeDepositInvoiceVoided: boolean; stripeFinalInvoiceVoided: boolean }
>(functions, 'cancelEngagement')

/** Provider portal: returns the calling member's open requests, current interest states, and confirmed engagement assignments. */
const getProviderInboxFn = createCallable<
  Record<string, never>,
  {
    member: { id: string; name: string; email: string | null }
    requests: Array<{
      id: string
      submitterName: string
      organizationName: string | null
      services: Array<{
        subType: string
        date: string | null
        location: string | null
        hours: number | null
      }>
      quoteTotal: string
      additionalInfo: string
      status: string
      createdAtMs: number | null
      myInterestStatus: string | null
    }>
    assignments: Array<{
      id: string
      title: string
      description: string | null
      scheduledDate: string | null
      status: string
      clientName: string
      clientEmail: string
      estimateNote: string | null
      equipmentNames: string[]
      lineItems: Array<{
        description: string
        qty: number | null
        unit: string | null
        date: string | null
        time: string | null
        location: string | null
      }>
      clientAdditionalInfo: string | null
    }>
  }
>(functions, 'getProviderInbox')

/** Provider portal: member marks themselves available / declined / withdrawn on a specific request. */
const setProviderInterestFn = createCallable<
  { requestId: string; status: 'available' | 'declined' | 'withdrawn' },
  { success: boolean; status: string }
>(functions, 'setProviderInterest')

/** Provider portal: submits or updates the calling member's tax profile (W-9 data). Tax ID is encrypted server-side. */
interface SubmitTaxProfileInput {
  legalName: string
  businessName?: string
  address: {
    line1: string
    line2?: string
    city: string
    state: string
    postalCode: string
  }
  entityType:
    | 'individual'
    | 'sole_prop'
    | 'llc_single'
    | 'llc_multi'
    | 's_corp'
    | 'c_corp'
    | 'partnership'
    | 'other'
  backupWithholding: boolean
  signatureImageUrl: string
  taxIdPlain: string
}
const submitTaxProfileFn = createCallable<SubmitTaxProfileInput, { ok: true }>(
  functions,
  'submitTaxProfile'
)

/** Approves a provider invoice only after the server verifies encrypted tax ID storage. */
const approveProviderInvoiceFn = httpsCallable<{ invoiceId: string }, { ok: true }>(
  functions,
  'approveProviderInvoice'
)

/** Refreshes the prepared estimate snapshot. Admin only. Disallowed once deposit invoice sent. */
const refreshEstimateFn = createCallable<{ engagementId: string }, { success: boolean }>(
  functions,
  'refreshEstimate'
)

export interface StripeConnectStatus {
  accountId: string
  accountType: string
  businessName: string | null
  businessUrl: string | null
  country: string | null
  defaultCurrency: string | null
  detailsSubmitted: boolean
  chargesEnabled: boolean
  payoutsEnabled: boolean
  currentlyDueCount: number
  pastDueCount: number
  disabledReason: string | null
  readyForClientBilling: boolean
}

const getStripeConnectStatusFn = createCallable<Record<string, never>, StripeConnectStatus>(
  functions,
  'getStripeConnectStatus'
)

const createStripeConnectOnboardingLinkFn = createCallable<
  Record<string, never>,
  { url: string; expiresAt: number }
>(functions, 'createStripeConnectOnboardingLink')

/** Sends the deposit invoice (or full invoice if ≤ threshold) through the stamped billing owner. Admin only. */
const sendDepositInvoiceFn = createCallable<
  { engagementId: string },
  { stripeInvoiceId: string; hostedInvoiceUrl: string | null }
>(functions, 'sendDepositInvoice')

/** Sends the final invoice after delivery; snapshots the engagement's current line items. Admin only. */
const sendFinalInvoiceFn = createCallable<
  { engagementId: string },
  { stripeInvoiceId: string; hostedInvoiceUrl: string | null }
>(functions, 'sendFinalInvoice')

const reconcileStripeEngagementFn = createCallable<
  { engagementId: string },
  { engagementId: string; healthy: boolean; items: Array<{ documentKind: string; error?: string }> }
>(functions, 'reconcileStripeEngagement')

const replayStripeEngagementEventFn = createCallable<
  { eventId: string },
  { success: boolean; eventId: string; eventType: string }
>(functions, 'replayStripeEngagementEvent')

/** Recomputes the press kit derived-stat cache. Client calls only when cache is missing/stale. */
const refreshPressKitStatCacheFn = createCallable<
  Record<string, never>,
  { values: Record<string, string>; refreshedAt: number; cached: boolean }
>(functions, 'refreshPressKitStatCache')

/** Server-authorized application upload returned by either public form flow. */
export interface ApplicationUploadReceipt {
  uploadId: string
  claimToken: string
  url: string
}

interface ApplicationUploadPrepareResult {
  uploadId: string
  claimToken: string
  objectPath: string
  downloadToken: string
  downloadUrl: string
}

const prepareApplicationUploadFn = createCallable<
  {
    applicationId: string
    formId: string
    fieldId: string
    fileName: string
    contentType: string
    sizeBytes: number
  },
  ApplicationUploadPrepareResult
>(functions, 'prepareApplicationUpload')

const prepareTechQuestionnaireUploadFn = createCallable<
  {
    productionSlug: string
    submissionId: string
    token: string
    applicationId: string
    formId: string
    fieldId: string
    fileName: string
    contentType: string
    sizeBytes: number
  },
  ApplicationUploadPrepareResult
>(functions, 'prepareTechQuestionnaireUpload')

const submitApplicationFn = createCallable<
  {
    applicationId: string
    formId: string
    applicantInfo: { firstName: string; lastName: string; email: string }
    answers: Record<string, { label: string; fieldLibraryId?: string; value: unknown }>
    fileUploads: Record<string, ApplicationUploadReceipt[]>
    termsAccepted: boolean
    submissionRequestId?: string
  },
  { submissionId: string }
>(functions, 'submitApplication')

/** Self-submitted testimonial (survey flow). Auth required, rate-limited. */
const submitTestimonialFn = createCallable<
  {
    engagementId: string
    clientUserId: string
    clientName: string
    clientEmail: string
    clientTitle?: string
    serviceType?: string
    rating: number
    content: string
  },
  { testimonialId: string }
>(functions, 'submitTestimonial')

/** Public service request submission. Rate-limited, App Check-aware. */
const submitServiceRequestFn = createCallable<
  {
    firstName: string
    lastName: string
    email: string
    organizationName?: string
    discountCategory: 'none' | 'student' | 'nonprofit' | 'odd_artist'
    discountAppliedPercent: number
    mailingOptIn: boolean
    services: unknown[]
    quoteTotal: string
    additionalInfo: string
    requestedProviders: string
    tosAccepted: boolean
    bookingAcknowledge: boolean
  },
  { requestId: string }
>(functions, 'submitServiceRequest')

/** Creates a Firebase Auth user + users/{uid} doc for an unregistered client and emails a password reset link. Admin only. */
const createClientUserByAdminFn = createCallable<
  { email: string; displayName: string },
  { uid: string; alreadyExisted: boolean }
>(functions, 'createClientUserByAdmin')

export interface AuthUserListItem {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  providerIds: string[]
  disabled: boolean
  createdAt: number | null
  lastSignInAt: number | null
  role: 'admin' | 'designer' | 'user' | 'staff' | null
  linkedMemberId: string | null
  hasProfile: boolean
}

/** Lists every Firebase Auth user joined with their /users Firestore doc. Admin only. */
const listAuthUsersFn = createCallable<
  Record<string, never>,
  { users: AuthUserListItem[]; total: number }
>(functions, 'listAuthUsers')

/** One-shot: creates /users docs for any Auth user missing one. Admin only. */
const backfillUserProfilesFn = createCallable<
  Record<string, never>,
  { scanned: number; created: number; alreadyPresent: number }
>(functions, 'backfillUserProfiles')

/** Re-asserts both sides of a user↔member link. Admin only. */
const repairUserMemberLinkFn = createCallable<
  { userId: string; memberId: string },
  { success: true }
>(functions, 'repairUserMemberLink')

/** Refreshes the signed-in user's Storage authorization claims. */
const syncAdminClaimsFn = createCallable<Record<string, never>, { isAdmin: boolean }>(
  functions,
  'syncAdminClaims'
)

/** Creates a project board for an engagement (idempotent — returns existing boardId if one is already linked). Admin only. */
const createProjectBoardFromEngagementFn = createCallable<
  { engagementId: string; templateOverrides?: Record<string, string> },
  { boardId: string }
>(functions, 'createProjectBoardFromEngagement')

/** Creates a nested child project board hanging off a task. Admin only. Enforces max depth 20. */
const createChildProjectBoardFn = createCallable<
  { parentBoardId: string; parentTaskId: string },
  { boardId: string }
>(functions, 'createChildProjectBoard')

/** Snapshots a board (including all descendant sub-boards) into a reusable BoardTemplate doc. Admin only. */
const saveBoardAsTemplateFn = createCallable<
  { boardId: string; name: string; description?: string; defaultForServiceIds?: string[] },
  { templateId: string }
>(functions, 'saveBoardAsTemplate')

/** Adds tasks (with seeded sub-boards) for any engagement services not yet present on an overview board. Admin only. */
const reconcileOverviewBoardFn = createCallable<
  { boardId: string; templateOverrides?: Record<string, string> },
  { added: number; addedServiceIds: string[] }
>(functions, 'reconcileOverviewBoard')

/** Provisions a new Google Drive folder for a client in the configured shared drive. Admin only. */
const provisionClientDriveFn = createCallable<
  { uid: string },
  { folderId: string; folderUrl: string; folderName: string }
>(functions, 'provisionClientDrive')

/** Validates a Drive folder URL or ID and links it to a client. Admin only. */
const linkClientDriveFn = createCallable<
  { uid: string; folderUrlOrId: string },
  { folderId: string; folderUrl: string; folderName: string }
>(functions, 'linkClientDrive')

/** Removes the clientDrive link from a user (does NOT delete the folder). Admin only. */
const unlinkClientDriveFn = createCallable<{ uid: string }, { success: true }>(
  functions,
  'unlinkClientDrive'
)

/** Admin-only: returns metadata + rendered sample HTML for every registered email template. */
export interface EmailTemplateSummary {
  id: string
  name: string
  description: string
  category: string
  trigger: string
  subject: string
  html: string
  sampleDataJson: string
}
const listEmailTemplatesFn = createCallable<
  Record<string, never>,
  { templates: EmailTemplateSummary[] }
>(functions, 'listEmailTemplates')

/** Admin-only: renders a template with its sample data and sends it to one recipient. */
const sendTestEmailFn = createCallable<
  { templateId: string; to: string },
  { ok: true; subject: string }
>(functions, 'sendTestEmail')

/** Creates (or reuses) the engagement's Drive subfolder under the client folder. Admin only. */
const createEngagementDriveFolderFn = createCallable<
  { engagementId: string },
  { folderId: string; folderUrl: string; folderName: string }
>(functions, 'createEngagementDriveFolder')

/**
 * Overture: finalize selections for a production. Sets confirmedAt on every
 * accepted submission and sends branded acceptance emails. Admin only.
 * Idempotent — already-confirmed submissions are skipped.
 */
const finalizeSelectionsFn = createCallable<
  { productionId: string; previewFingerprint: string },
  {
    confirmedCount: number
    skippedCount: number
    failedCount: number
    pendingCount?: number
    uncertainCount?: number
  }
>(functions, 'finalizeSelections')

const previewFinalizeSelectionsFn = createCallable<{ productionId: string }, FinalizationPreview>(
  functions,
  'previewFinalizeSelections'
)
const configureProductionRequirementsFn = createCallable<
  {
    productionId: string
    techApplicationId: string
    requirements: ParticipantRequirement[]
    summaryFieldIds: string[]
    replyToEmail: string
    expectedVersion: number
  },
  { ok: true }
>(functions, 'configureProductionRequirements')
const getProductionDeliveryAdminFn = createCallable<
  { submissionId: string },
  {
    requirements: ParticipantRequirement[]
    version: number
    deliverables: Record<string, Deliverable>
  }
>(functions, 'getProductionDeliveryAdmin')
const reviewProductionDeliverableFn = createCallable<
  {
    submissionId: string
    requirementId: string
    revision: number
    status: 'verified' | 'needs_changes'
    note: string
    archiveLocation: string
  },
  { ok: true }
>(functions, 'reviewProductionDeliverable')

/** Public: load production + applicant info for the /productions/:slug/confirm page. */
const getProductionConfirmationContextFn = createCallable<
  { productionSlug: string; submissionId: string; token: string },
  {
    productionName: string
    productionType: string
    venue: string
    startDate: string
    endDate?: string
    applicantFirstName: string
    applicantLastName: string
    currentResponse: 'pending' | 'accepted' | 'declined'
    hasTechQuestionnaire: boolean
    techCompleted: boolean
  }
>(functions, 'getProductionConfirmationContext')

/** Public: load the tech-questionnaire schema for the inline form on the confirm page. */
const getTechQuestionnaireSchemaFn = createCallable<
  { productionSlug: string; submissionId: string; token: string },
  {
    techApplicationId: string
    techApplicationTitle: string
    techApplicationDescription: string
    formId: string
    sections: unknown[]
    alreadySubmitted: boolean
    answers: Record<string, { label: string; value: string | string[] | number | boolean | null }>
    revision: number
    requirements: ParticipantRequirement[]
    requirementVersion: number
    deliverables: Record<string, Deliverable>
    intakeSummary: Array<{ label: string; value: unknown }>
  }
>(functions, 'getTechQuestionnaireSchema')

/** Public: submit the inline tech questionnaire. */
const submitTechQuestionnaireFn = createCallable<
  {
    productionSlug: string
    submissionId: string
    token: string
    answers: Record<string, { label: string; fieldLibraryId?: string; value: unknown }>
    fileUploads: Record<string, ApplicationUploadReceipt[]>
    expectedRevision?: number
  },
  { ok: true; techSubmissionId: string }
>(functions, 'submitTechQuestionnaire')

/** Public: artist accepts or declines a production offer via the confirm page. */
const confirmProductionResponseFn = createCallable<
  { productionSlug: string; submissionId: string; token: string; response: 'accept' | 'decline' },
  { ok: true; newResponse: 'accepted' | 'declined' }
>(functions, 'confirmProductionResponse')

/**
 * Admin-only manual override of the scheduled acceptance reminder. Bypasses the
 * 48h gap but still honors the 3-reminder cap. Counts mirror the scheduled fn.
 */
const sendAcceptanceReminderNowFn = createCallable<
  { submissionId: string },
  { sent: number; skipped: number; failed: number; pending: number; uncertain: number }
>(functions, 'sendAcceptanceReminderNow')

/** Admin-only manual override for the tech questionnaire reminder. */
const sendTechReminderNowFn = createCallable<
  { submissionId: string },
  { sent: number; skipped: number; failed: number; pending: number; uncertain: number }
>(functions, 'sendTechReminderNow')

/** Admin-only participant package configuration. The callable is the source of truth for production settings. */
const configureParticipantPackageFn = createCallable<
  {
    productionId: string
    config: ParticipantPackageConfig
    intendedParticipantSlots: number
  },
  { ok: true }
>(functions, 'configureParticipantPackage')

export interface ParticipantAdminOfferSummary {
  submissionId: string
  status: ParticipantOfferStatus
  slotId: string
  email: string
  waitlistPosition?: number
  paymentDueAt?: string
  reservationId?: string
  orderId?: string
}

export interface ParticipantPackageAdminContext {
  productionId: string
  production: {
    participantPackage?: ParticipantPackageConfig
    participantRosterState?: ParticipantRosterState
    intendedParticipantSlots?: number
  }
  offers: ParticipantAdminOfferSummary[]
}

/** Admin-only, sanitized operational view of participant offers and roster state. */
const getParticipantPackageAdminContextFn = createCallable<
  { productionId: string },
  ParticipantPackageAdminContext
>(functions, 'getParticipantPackageAdminContext')

export interface ParticipantRosterGateResult {
  state: ParticipantRosterState
  outstandingSlots: string[]
}

/** Locks the participant roster after all intended slots are paid. */
const lockParticipantRosterFn = createCallable<
  { productionId: string },
  ParticipantRosterGateResult
>(functions, 'lockParticipantRosterCallable')

/** Opens public ticket sales after the participant roster has been locked. */
const openParticipantPublicSalesFn = createCallable<
  { productionId: string },
  ParticipantRosterGateResult
>(functions, 'openParticipantPublicSalesCallable')

/** Admin recovery/operations action for a vacant slot. */
const promoteNextWaitlistedFn = createCallable<
  { productionId: string; slotId: string },
  { kind: string; submissionId?: string; invitationId?: string }
>(functions, 'promoteNextWaitlistedCallable')

/** Public newsletter signup / opt-in. Rate-limited, App Check-aware. */
const submitNewsletterSignupFn = createCallable<
  {
    email: string
    firstName?: string | null
    lastName?: string | null
    source:
      | 'footer-signup'
      | 'service-request'
      | 'application'
      | 'admin-manual'
      | 'admin-bulk'
      | 'admin-csv'
    segments?: Array<'general' | 'film-updates' | 'services' | 'oddfest' | 'shift'>
    language?: string | null
    sourceRequestId?: string
  },
  { result: 'added' | 'already-subscribed' }
>(functions, 'submitNewsletterSignup')

export {
  functions,
  createCallable,
  setApplicationDeadline,
  syncPrintifyProductsFn,
  createPaymentIntentFn,
  createTicketPaymentIntentFn,
  getOrderReceiptFn,
  getPublicTicketAvailabilityFn,
  requestTicketOrdersByEmailFn,
  createApplicationPaymentIntentFn,
  checkInTicketFn,
  issueRefundFn,
  assignStaffToEventFn,
  prepareEstimateFn,
  refreshEstimateFn,
  getStripeConnectStatusFn,
  createStripeConnectOnboardingLinkFn,
  sendEstimateToClientFn,
  cancelEngagementFn,
  getProviderInboxFn,
  setProviderInterestFn,
  submitTaxProfileFn,
  approveProviderInvoiceFn,
  sendDepositInvoiceFn,
  sendFinalInvoiceFn,
  reconcileStripeEngagementFn,
  replayStripeEngagementEventFn,
  refreshPressKitStatCacheFn,
  submitNewsletterSignupFn,
  submitServiceRequestFn,
  submitTestimonialFn,
  submitApplicationFn,
  prepareApplicationUploadFn,
  prepareTechQuestionnaireUploadFn,
  createClientUserByAdminFn,
  listAuthUsersFn,
  backfillUserProfilesFn,
  repairUserMemberLinkFn,
  syncAdminClaimsFn,
  createProjectBoardFromEngagementFn,
  createChildProjectBoardFn,
  saveBoardAsTemplateFn,
  reconcileOverviewBoardFn,
  provisionClientDriveFn,
  linkClientDriveFn,
  unlinkClientDriveFn,
  createEngagementDriveFolderFn,
  listEmailTemplatesFn,
  sendTestEmailFn,
  finalizeSelectionsFn,
  previewFinalizeSelectionsFn,
  configureProductionRequirementsFn,
  getProductionDeliveryAdminFn,
  reviewProductionDeliverableFn,
  getProductionConfirmationContextFn,
  confirmProductionResponseFn,
  sendAcceptanceReminderNowFn,
  sendTechReminderNowFn,
  getTechQuestionnaireSchemaFn,
  submitTechQuestionnaireFn,
  configureParticipantPackageFn,
  getParticipantPackageAdminContextFn,
  lockParticipantRosterFn,
  openParticipantPublicSalesFn,
  promoteNextWaitlistedFn,
}

export const reconcileProductionInvitationFn = createCallable<
  { submissionId: string; providerMessageId: string },
  { ok: true; providerEvent: string }
>(functions, 'reconcileProductionInvitation')
